import { SessionStatus } from '@/API';
import LoadingDots from '@/components/Animated/Icons/LoadingDots';
import HostGame from '@/components/Host/HostGame';
import HostGameSummary from '@/components/Host/HostGameSummary';
import HostGameWaitingAnswer from '@/components/Host/HostGameWaitingAnswer';
import HostLobby from '@/components/Host/HostLobby';
import { useSession, useState as useHostState } from '@/hooks/HostSessionControlHooks';
import { useAuth } from '@/providers/AuthProvider';
import { useZoom } from '@/providers/ZoomProvider';
import IntermidiateStep from '@/views/IntermidiateStep';
import { useEffect, useState } from 'react';

export default function Session() {
  // Session
  const state = useHostState();
  const session = useSession();
  const [dismissableTips, setDismissableTips] = useState(false);
  const [tipsEnabled, setTipsEnabled] = useState(true);
  const { hostData, updateHostData } = useAuth();
  const zoom = useZoom();

  useEffect(() => {
    if (dismissableTips) {
      return;
    }

    if (!hostData?.hasHostedGame) {
      updateHostData({
        hasHostedGame: true
      });
    }

    setDismissableTips(hostData?.hasHostedGame ?? false);
  }, []);

  useEffect(() => {
    if (!hostData) return;

    setTipsEnabled(hostData.hostTipsEnabled);
  }, [hostData?.hostTipsEnabled]);

  const handleTipsDismissed = () => {
    setTipsEnabled(false);
    updateHostData({ hostTipsEnabled: false });
  };

  function renderGameState() {
    // State Base: Loading
    if (state === 'initializing') {
      return (
        <div className="flex items-center justify-center h-screen">
          <LoadingDots />
        </div>
      );
    }

    // State Base: Disconnected
    if (state === 'disconnected') {
      return (
        <IntermidiateStep
          title="Disconnected"
          subtitle="Please check your internet connection. Attempting to reconnect..."
        />
      );
    }

    // State 1: Session created
    if (session?.status === SessionStatus.created) {
      return (
        <HostLobby
          dismissableTips={dismissableTips}
          tipsEnabled={tipsEnabled}
          onTipsDismissed={handleTipsDismissed}
        />
      );
    }

    // State 2: Session started
    if (session?.status == SessionStatus.drawing || session?.status == SessionStatus.sharing) {
      return (
        <HostGame
          dismissableTips={dismissableTips}
          tipsEnabled={tipsEnabled}
          onTipsDismissed={handleTipsDismissed}
        />
      );
    }

    // State 3: Session ending (waiting for player answers)
    if (session?.status == SessionStatus.ending) {
      return <HostGameWaitingAnswer />;
    }

    // State 4: Session ended
    if (session?.status == SessionStatus.ended) {
      return <HostGameSummary />;
    }

    return <></>;
  }

  // Inside Zoom the available height is the app panel, not the viewport, and content
  // must be scrollable rather than clipped.
  return (
    <div className={zoom.embedded ? 'h-screen overflow-y-auto' : 'h-screen'}>
      {renderGameState()}
    </div>
  );
}
